/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import {
  func, shape, string, arrayOf, bool, number
} from 'prop-types';
import { extend } from '@thd-nucleus/data-sources';
import { useImpression } from '@thd-olt-component-react/impression';
import {
  Card,
  CardBody,
  CardTitle,
  Tooltip,
  Link,
  Typography,
  Badge
} from '@one-thd/sui-atomic-components';
import { MoreButton } from '@thd-olt-component-react/project-forms';
import { Apron } from '@one-thd/sui-icons';
import { Thumbnails } from './Thumbnails';
import { ProjectTilePlaceholder } from './ProjectTilePlaceholder';
import { PROJECT_STATUS_ACTIVE, PROJECT_STATUS_ARCHIVED } from '../project-summary-helper';
import { dateModifiedMessage } from '../../helpers';

export const ProjectTile = ({
  projectId, project, onDelete, position, loading, disableAction, onClick
}) => {
  const [shortProjectName, setShortProjectName] = useState('');
  const projectStatus = project.status;
  const statusText = projectStatus === PROJECT_STATUS_ACTIVE ? PROJECT_STATUS_ACTIVE : PROJECT_STATUS_ARCHIVED;
  const statusBadgeColor = projectStatus === PROJECT_STATUS_ACTIVE ? 'info' : 'medium';
  const projectName = project?.projectName ?? '';
  const projectNameIsLong = projectName && projectName.length > 20;
  const modifyDateTime = project?.modifyDateTime ?? '';

  const cutThreeTimesProjectName = projectName && projectName.length > 40;
  const firstPartProjectName = projectName.slice(0, 20);
  const secondPartProjectName = projectName.slice(20, 40);
  const thirdPartProjectName = projectName.slice(40, projectName.length);

  const projectNameSpliced = cutThreeTimesProjectName
    ? `${firstPartProjectName}
    ${secondPartProjectName}
    ${thirdPartProjectName}
    `
    : `${firstPartProjectName}
    ${secondPartProjectName}
    `;

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('project-tile.ready');
  }, []);

  useEffect(() => {
    if (projectNameIsLong) {
      const projectNameSubStr = projectName.slice(0, 17);
      const newProjectNameWithEllipsis = projectNameSubStr.concat('...');

      setShortProjectName(newProjectNameWithEllipsis);
    }
  }, [projectNameIsLong]);

  // const onDeleteConfirmation = () => {
  //   if (onDelete) {
  //     onDelete({ projectId });
  //   }
  // };

  const impressionContextData = useImpression({ data: {
    id: projectId,
    name: 'ProjectTile',
    component: 'ProjectTile',
    position: position + 1,
    type: 'content'
  } });

  if (!project && loading) return <ProjectTilePlaceholder />;

  if (!project) return null;

  const projectTileRef = impressionContextData.ref;
  const projectTileClickId = impressionContextData.clickID;

  const url = `/projects/${project?.id}`;

  const headerWithoutTooltip = (
    <a
      href={url}
      onClick={() => onClick()}
    >
      <Typography
        variant="h3"
        weight="bold"
        color={projectStatus === PROJECT_STATUS_ACTIVE ? 'primary' : 'subtle'}
      >
        {projectNameIsLong ? shortProjectName : projectName}
      </Typography>
    </a>
  );

  const headerWithTooltip = (
    <Tooltip
      placement="top"
      title={projectNameSpliced}
    >
      <a href={url} onClick={() => onClick()}>
        <Typography
          variant="h3"
          weight="bold"
          color={projectStatus === PROJECT_STATUS_ACTIVE ? 'primary' : 'subtle'}
        >
          {shortProjectName}
        </Typography>
      </a>
    </Tooltip>
  );
  const header = (projectNameIsLong) ? headerWithTooltip : headerWithoutTooltip;

  const itemCountMessage = `${project?.numberOfItems || 0} ${project?.numberOfItems === 1 ? ' item' : ' items'}`;

  return (
    // eslint-disable-next-line react/no-unknown-property
    <div className="sui-w-full" ref={projectTileRef} clickid={projectTileClickId} data-component="ProjectTile" data-testid="project-tile">
      <Card className="sui-grid sui-rounded-md sui-shadow-base sui-h-full sui-my-0">
        <CardBody>
          <div className="sui-flex sui-mb-2">
            <Badge data-testid="badge-status" size="small" variant="subtle" color={statusBadgeColor}>
              <span className="sui-capitalize">
                {statusText}
              </span>
            </Badge>
          </div>
          <CardTitle
            header={(header)}
            subheader={<Link href={url} onClick={() => onClick()} className="sui-font-regular sui-text-base">Project Number: {project?.projectId}</Link>}
            action={!disableAction && (
              <MoreButton
                projectStatus={projectStatus}
                projectName={projectName}
                projectId={projectId}
              />
            )}
            disableActionSpacing
          />
        </CardBody>
        <CardBody>
          <Thumbnails url={url} thumbnails={project?.images} />
        </CardBody>
        <CardBody>
          {project.isCreatedByTHDAssociate
            ? (
              <div className="sui-flex sui-items-baseline">
                <Typography variant="body-xs" color={projectStatus === PROJECT_STATUS_ACTIVE ? 'primary' : 'subtle'}>
                  Created By:
                </Typography>
                <div className="sui-mr-2 sui-ml-2">
                  <Apron color="brand" size="small" />
                </div>
                <Typography variant="body-xs" color={projectStatus === PROJECT_STATUS_ACTIVE ? 'primary' : 'subtle'}>
                  THD Associate
                </Typography>
              </div>
            )
            : <Typography variant="body-xs" color={projectStatus === PROJECT_STATUS_ACTIVE ? 'primary' : 'subtle'}>Created By: {project?.createdByName}</Typography>}
          <Typography variant="body-xs" color={projectStatus === PROJECT_STATUS_ACTIVE ? 'primary' : 'subtle'}>{itemCountMessage}</Typography>
          <Typography variant="body-xs" color="subtle">Last Modified: {dateModifiedMessage(modifyDateTime)}</Typography>
        </CardBody>
      </Card>
    </div>
  );
};

ProjectTile.displayName = 'ProjectTile';

ProjectTile.propTypes = {
  projectId: string.isRequired,
  onDelete: func,
  project: shape({
    projectId: string,
    projectName: string,
    status: string,
    modifyDateTime: string,
    createdBy: string,
    createdByName: string,
    isCreatedByTHDAssociate: bool,
    numberOfItems: number,
    images: arrayOf(string)
  }),
  position: number,
  loading: bool,
  disableAction: bool,
  onClick: func
};

ProjectTile.defaultProps = {
  onDelete: null,
  project: null,
  position: 0,
  loading: true,
  disableAction: false,
  onClick: () => {}
};

ProjectTile.dataModel = extend(
  MoreButton
);
